import axios from "axios";

export type PagerDutyAccountId = string & { __brand: "PagerDutyAccountId" };
export type PagerDutyBindingId = string & { __brand: "PagerDutyBindingId" };

export type PagerDutyAccount = {
    id: PagerDutyAccountId;
    name: string;
    enabled: boolean;
    /** False until PagerDuty has issued a secret for this account's webhook URL. */
    hasSigningSecret: boolean;
    webhookUrl: string;
    /** Null posts as the PagerDuty system user. */
    postsAsUserId: string | null;
    postsAsName: string | null;
    commentOnAcknowledge: boolean;
    commentOnAnnotate: boolean;
    resolveOnIncidentResolve: boolean;
    reopenOnRetrigger: boolean;
};

export type PagerDutyAccountInput = {
    name: string;
    /** Null keeps whatever is stored, including nothing at all. */
    signingSecret: string | null;
    enabled: boolean;
    postsAsUserId: string | null;
    commentOnAcknowledge: boolean;
    commentOnAnnotate: boolean;
    resolveOnIncidentResolve: boolean;
    reopenOnRetrigger: boolean;
};

export type PagerDutyQueuePriority = {
    id: string;
    name: string;
};

export type PagerDutyQueue = {
    portalId: string;
    portalName: string;
    projectId: string;
    projectName: string;
    priorities: PagerDutyQueuePriority[];
};

export type PagerDutyBinding = {
    id: PagerDutyBindingId;
    accountId: PagerDutyAccountId;
    accountName: string;
    /** Null is the account's catch-all: every service its subscription delivers. */
    serviceId: string | null;
    serviceName: string | null;
    portalId: string;
    portalName: string;
    projectId: string;
    projectName: string;
    priorityId: string | null;
    priorityName: string | null;
};

export type PagerDutyBindingInput = {
    accountId: PagerDutyAccountId;
    /** Null routes every service the account's subscription delivers. */
    serviceId: string | null;
    serviceName: string | null;
    portalId: string;
    projectId: string;
    priorityId: string | null;
};

export type PagerDutyDelivery = {
    id: string;
    accountName: string | null;
    received: string;
    eventType: string;
    incidentId: string | null;
    serviceName: string | null;
    summary: string | null;
    result: string;
    ticketKey: string | null;
};

export type PagerDutyIndex = {
    accounts: PagerDutyAccount[];
    bindings: PagerDutyBinding[];
    queues: PagerDutyQueue[];
    deliveries: PagerDutyDelivery[];
};

export interface PagerDutyApi {
    get(): Promise<PagerDutyIndex>;
    createAccount(input: PagerDutyAccountInput): Promise<PagerDutyAccount>;
    updateAccount(id: PagerDutyAccountId, input: PagerDutyAccountInput): Promise<PagerDutyAccount>;
    removeAccount(id: PagerDutyAccountId): Promise<void>;
    createBinding(input: PagerDutyBindingInput): Promise<PagerDutyBinding>;
    updateBinding(id: PagerDutyBindingId, input: PagerDutyBindingInput): Promise<PagerDutyBinding>;
    removeBinding(id: PagerDutyBindingId): Promise<void>;
}

export const pagerDutyApi: PagerDutyApi = {
    async get() {
        const { data } = await axios.get<PagerDutyIndex>("/api/v0/pagerduty");
        return data;
    },
    async createAccount(input) {
        const { data } = await axios.post<PagerDutyAccount>("/api/v0/pagerduty/accounts", input);
        return data;
    },
    async updateAccount(id, input) {
        const { data } = await axios.put<PagerDutyAccount>(
            `/api/v0/pagerduty/accounts/${id}`,
            input
        );
        return data;
    },
    async removeAccount(id) {
        await axios.delete(`/api/v0/pagerduty/accounts/${id}`);
    },
    async createBinding(input) {
        const { data } = await axios.post<PagerDutyBinding>("/api/v0/pagerduty/bindings", input);
        return data;
    },
    async updateBinding(id, input) {
        const { data } = await axios.put<PagerDutyBinding>(
            `/api/v0/pagerduty/bindings/${id}`,
            input
        );
        return data;
    },
    async removeBinding(id) {
        await axios.delete(`/api/v0/pagerduty/bindings/${id}`);
    },
};
